/**
 * <s-quick-calc> — free "what's left?" calculator on the homepage.
 *
 * Reads the visitor's order numbers and shows what is left before ads, the
 * margin, and the break-even ROAS. Everything is computed in the browser;
 * nothing is sent anywhere.
 *
 * Formula (matches the example on the homepage):
 *   net      = price / (1 + vat)          price excl. VAT
 *   fee      = price × fee%               payment fee on the paid amount
 *   left     = net − cost − shipping − fee
 *   margin   = left / net
 *   roas     = price / left               break-even ROAS on revenue incl. VAT
 *
 * Required markup:
 *   input[data-calc-input="price|cost|shipping|fee"]  — decimal inputs (comma or dot)
 *   select[data-calc-input="vat"]                      — VAT rate in percent
 *   [data-calc-output="left|margin|roas"]              — result elements
 *   [data-calc-result]                                 — wrapper toggled with data-loss
 *   [data-calc-loss]                                   — message shown when left ≤ 0
 * Optional attributes on the element:
 *   data-currency-unit — suffix for money values (default "kr")
 */
class SQuickCalc extends HTMLElement {
  #ac;
  #inputs = {};
  #outputs = {};
  #result;
  #loss;
  #money;
  #number;

  connectedCallback() {
    this.#ac = new AbortController();
    const locale = document.documentElement.lang || 'sv';
    this.#money = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    this.#number = new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

    for (const el of this.querySelectorAll('[data-calc-input]')) {
      this.#inputs[el.dataset.calcInput] = el;
    }
    for (const el of this.querySelectorAll('[data-calc-output]')) {
      this.#outputs[el.dataset.calcOutput] = el;
    }
    this.#result = this.querySelector('[data-calc-result]');
    this.#loss = this.querySelector('[data-calc-loss]');

    this.addEventListener('input', this.#update, { signal: this.#ac.signal });
    this.addEventListener('change', this.#update, { signal: this.#ac.signal });
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #read(name) {
    const raw = this.#inputs[name]?.value ?? '';
    const value = Number.parseFloat(String(raw).replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  #update = () => {
    const unit = this.dataset.currencyUnit || 'kr';
    const price = this.#read('price');
    const cost = this.#read('cost');
    const shipping = this.#read('shipping');
    const feeRate = this.#read('fee') / 100;
    const vatRate = this.#read('vat') / 100;

    const net = price / (1 + vatRate);
    const fee = price * feeRate;
    const left = net - cost - shipping - fee;
    const hasPrice = price > 0;
    const isLoss = hasPrice && left <= 0;

    this.#set('left', hasPrice ? `${this.#money.format(left)} ${unit}` : '–');
    this.#set('margin', hasPrice && net > 0 ? `${this.#number.format((left / net) * 100)} %` : '–');
    this.#set('roas', hasPrice && !isLoss ? `${this.#money.format(price / left)}x` : '–');

    if (this.#result) this.#result.toggleAttribute('data-loss', isLoss);
    if (this.#loss) this.#loss.hidden = !isLoss;
  };

  #set(name, text) {
    const el = this.#outputs[name];
    if (el && el.textContent !== text) el.textContent = text;
  }
}

if (!customElements.get('s-quick-calc')) {
  customElements.define('s-quick-calc', SQuickCalc);
}
